// src\app\[lang]\components\LanguageSwitcher.tsx
'use client';
import { useState, useCallback } from 'react';
import Link from 'next/link';
import { Button, Menu, MenuItem } from '@mui/material';
import { ChevronDownIcon as KeyboardArrowDownIcon, GlobeAltIcon as LanguageIcon, CheckIcon } from '@heroicons/react/24/solid';
import { SxProps, Theme } from '@mui/material/styles';

interface LanguageSwitcherProps {
  currentLang: string;
  sx?: SxProps<Theme>;
  isMobile?: boolean;
}

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' }
] as const;

const LanguageSwitcher = ({ currentLang, sx, isMobile = false }: LanguageSwitcherProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const currentLanguage = LANGUAGES.find(lang => lang.code === currentLang) || LANGUAGES[0];

  return (
    <div style={{ width: isMobile ? '100%' : 'auto' }}>
      <Button
        id="language-button"
        onClick={handleClick}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        aria-controls={anchorEl ? 'language-menu' : undefined}
        aria-label={`${currentLang === 'fr' ? 'Langue' : 'Language'}: ${currentLanguage.label}`}
        startIcon={!isMobile ? <LanguageIcon className="w-5 h-5" /> : undefined}
        endIcon={<KeyboardArrowDownIcon className={isMobile ? "w-4 h-4" : "w-5 h-5"} />}
        size="small"
        sx={{
          color: 'text.primary',
          textTransform: 'none',
          fontSize: isMobile ? '0.813rem' : '0.875rem',
          width: isMobile ? '100%' : 'auto',
          justifyContent: isMobile ? 'flex-start' : 'center',
          py: isMobile ? 1.5 : 0,
          px: isMobile ? 2 : 1,
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          ...(isMobile && {
            '& .MuiButton-endIcon svg': {
              width: '1rem',
              height: '1rem',
            }
          }),
          ...sx
        }}
      >
        {currentLanguage.label}
      </Button>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableScrollLock={true}
        sx={{
          ...(isMobile && {
            '& .MuiMenu-paper': {
              width: '240px', // Match drawer width
              left: '0px !important',
              maxWidth: '100%',
            },
            '& .MuiList-root': {
              padding: 0,
            },
            '& svg': {
              width: '1rem',
              height: '1rem',
            }
          })
        }}
        MenuListProps={{
          'aria-labelledby': 'language-button',
          sx: {
            ...(isMobile && {
              padding: 0,
            })
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isMobile ? 'left' : 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isMobile ? 'left' : 'center',
        }}
      >
        {LANGUAGES.map((language) => (
          <MenuItem
          key={language.code}
          component={Link}
          href={`/${language.code}`}
          onClick={handleClose}
          selected={currentLang === language.code}
          sx={{
            justifyContent: 'space-between', // Changed to space-between for icon placement
            py: isMobile ? 1.5 : 1,
            width: '100%',
            fontSize: isMobile ? '0.813rem' : '0.875rem', // Matching button text size
            backgroundColor: currentLang === language.code ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
            '&:hover': {
              backgroundColor: currentLang === language.code 
                ? 'rgba(0, 0, 0, 0.08)' 
                : 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          {language.label}
          {currentLang === language.code && (
            <CheckIcon className={isMobile ? "w-3.5 h-3.5 ml-1" : "w-4 h-4 ml-1"} aria-hidden="true" />
          )}
        </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default LanguageSwitcher;